import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Settings2, AlertTriangle, Save } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminApi, type SerpSettingItem } from "@/api";

interface SettingState {
  calculate_rectangles: boolean;
  load_async_ai_overview: boolean;
  default_depth: number;
  search_operator_warning: boolean;
}

function parseBool(val: unknown): boolean {
  return val === true || val === "true";
}

function parseSettingsState(items: SerpSettingItem[]): SettingState {
  const map = new Map(items.map((s) => [s.setting_key, s.setting_value]));
  return {
    calculate_rectangles: parseBool(map.get("calculate_rectangles")),
    load_async_ai_overview: parseBool(map.get("load_async_ai_overview")),
    default_depth: Number(map.get("default_depth")) || 100,
    search_operator_warning: parseBool(map.get("search_operator_warning")),
  };
}

export default function SerpSettings() {
  const queryClient = useQueryClient();
  const [state, setState] = useState<SettingState>({
    calculate_rectangles: false,
    load_async_ai_overview: false,
    default_depth: 100,
    search_operator_warning: true,
  });

  const { data: settings, isLoading } = useQuery<SerpSettingItem[]>({
    queryKey: ["serp-settings"],
    queryFn: async () => {
      const res = await adminApi.getSerpSettings();
      return res.data || [];
    },
  });

  useEffect(() => {
    if (settings) {
      setState(parseSettingsState(settings));
    }
  }, [settings]);

  const mutation = useMutation({
    mutationFn: () =>
      adminApi.updateSerpSettings([
        { key: "calculate_rectangles", value: state.calculate_rectangles },
        { key: "load_async_ai_overview", value: state.load_async_ai_overview },
        { key: "default_depth", value: state.default_depth },
        { key: "search_operator_warning", value: state.search_operator_warning },
      ]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serp-settings"] });
      toast.success("SERP settings updated");
    },
    onError: (err: Error) => toast.error(`Update failed: ${err.message}`),
  });

  // Cost estimation
  const baseCost = 0.002;
  const depthMultiplier = state.default_depth / 10;
  const rectMultiplier = state.calculate_rectangles ? 2 : 1;
  const aiOverviewExtra = state.load_async_ai_overview ? 0.0006 : 0;
  const costPerQuery = baseCost * depthMultiplier * rectMultiplier + aiOverviewExtra;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">SERP Advanced Settings</h1>
        <p className="text-muted-foreground">DataForSEO Advanced API parameters</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-primary" />
            <CardTitle>DataForSEO Parameters</CardTitle>
          </div>
          <CardDescription>
            Controls for the Advanced SERP API. Changes apply to all future queries.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* calculate_rectangles */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-1">
              <Label className="font-medium">calculate_rectangles</Label>
              <p className="text-sm text-muted-foreground">
                Calculate element position coordinates for SERP visual analysis.
              </p>
              <Badge variant="destructive" className="text-xs">Cost x2</Badge>
            </div>
            <Switch
              checked={state.calculate_rectangles}
              onCheckedChange={(v) => setState((s) => ({ ...s, calculate_rectangles: v }))}
            />
          </div>

          {/* load_async_ai_overview */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-1">
              <Label className="font-medium">load_async_ai_overview</Label>
              <p className="text-sm text-muted-foreground">
                Load AI Overview (AI-generated search results) full content.
              </p>
              <Badge variant="secondary" className="text-xs">+$0.0006/request</Badge>
            </div>
            <Switch
              checked={state.load_async_ai_overview}
              onCheckedChange={(v) => setState((s) => ({ ...s, load_async_ai_overview: v }))}
            />
          </div>

          {/* search_operator_warning */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                <Label className="font-medium">Search Operator Warning</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Show cost x5 warning when using site: / inurl: / intitle: operators.
              </p>
            </div>
            <Switch
              checked={state.search_operator_warning}
              onCheckedChange={(v) => setState((s) => ({ ...s, search_operator_warning: v }))}
            />
          </div>

          {/* default_depth */}
          <div className="rounded-lg border p-4 space-y-3">
            <div className="space-y-1">
              <Label className="font-medium">Default Search Depth</Label>
              <p className="text-sm text-muted-foreground">
                Number of results per query (multiple of 10). 10 = base price, 100 = x10.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min={10}
                max={700}
                step={10}
                value={state.default_depth}
                onChange={(e) => {
                  const v = Math.max(10, Math.min(700, parseInt(e.target.value) || 100));
                  setState((s) => ({ ...s, default_depth: v }));
                }}
                className="w-24"
              />
              <span className="text-sm text-muted-foreground">results</span>
            </div>
          </div>

          {/* Cost estimation */}
          <div className="rounded-lg bg-muted/50 p-4 space-y-2">
            <Label className="font-medium text-sm">Estimated Cost per Query (Straico Credits)</Label>
            <div className="text-2xl font-bold">{costPerQuery.toFixed(4)} Credits</div>
            <div className="text-xs text-muted-foreground space-y-1">
              <div>Base: 0.002 x {depthMultiplier} (depth) {state.calculate_rectangles ? "x 2 (rectangles)" : ""} {state.load_async_ai_overview ? `+ 0.0006 (AI Overview)` : ""}</div>
              <div>Monthly estimate (500 queries): <span className="font-medium">{(costPerQuery * 500).toFixed(2)} Credits</span></div>
            </div>
          </div>

          {/* Save button */}
          <Button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="w-full"
          >
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Settings
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
